import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import QRCode from 'react-qr-code';
import { useHistory } from 'react-router-dom';
import QuickCamLogo from '../../assets/svg/quickcam_logo.svg';
import QRCenterLogo from '../../assets/images/QRCenterLogo.png';
import { getKeycloakLoginUrl } from '../../environments';
import { startProfileSignup } from '../premium-profile/signup-prompt';
import {
  ACTIVE_QR_BORDER_VARIANT,
  getQrBorderSpec,
  QR_CENTER_MARK_BORDER,
  qrCenterMarkOutline,
  QrBorderVariant,
} from './qr-border-styles';
import QrModuleBorder from './qr-module-border';
import { downloadQrPng, qrDownloadName } from './qr-png';

export { profileQrBgColor, profileQrFgColor } from './qr-border-styles';

type QrKind = 'quickcam' | 'quicklinks';

type ProfileMenuProps = {
  channelName: string;
  profileImageURL: string;
  displayName: string;
  showAuth: boolean;
  premium: boolean;
};

export function qrCenterImage(
  kind: QrKind,
  profileImageURL: string,
  premium: boolean,
): string {
  if (kind === 'quickcam') return QRCenterLogo;
  if (!premium) return QuickCamLogo;
  const picture = profileImageURL.trim();
  return picture || QuickCamLogo;
}

function profileOrigin(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin.replace(/\/$/, '');
}

const profileQrSize = 200;
const profileQrMark = 66;
const quickcamMark = { width: 100, height: 80, code: 300 };

export function ProfileQrCode({
  value,
  center,
  alt,
  centerIsPhoto,
  markRadius,
  size,
  borderVariant = ACTIVE_QR_BORDER_VARIANT,
}: {
  value: string;
  center: string;
  alt: string;
  centerIsPhoto: boolean;
  markRadius: string | number;
  size: number;
  borderVariant?: QrBorderVariant;
}): JSX.Element {
  const palette = getQrBorderSpec(borderVariant);
  const mark = Math.round((profileQrMark * size) / profileQrSize);
  const markWidth = centerIsPhoto ? mark : Math.round((quickcamMark.width * size) / quickcamMark.code);
  const markHeight = centerIsPhoto ? mark : Math.round((quickcamMark.height * size) / quickcamMark.code);
  const centerOutline = qrCenterMarkOutline(centerIsPhoto, markRadius);
  return (
    <QrModuleBorder variant={borderVariant}>
      <Box sx={{ position: 'relative', width: size, height: size }}>
        <QRCode
          value={value}
          size={size}
          bgColor={palette.qrBackgroundColor}
          fgColor={palette.qrForegroundColor}
          level="H"
        />
        <Box
          component="img"
          src={center}
          alt={alt}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: markWidth,
            height: markHeight,
            objectFit: centerIsPhoto ? 'cover' : 'contain',
            borderRadius: markRadius,
            transform: 'translate(-50%, -50%)',
            boxSizing: 'border-box',
            ...(centerOutline ? { border: QR_CENTER_MARK_BORDER } : {}),
          }}
        />
      </Box>
    </QrModuleBorder>
  );
}

ProfileQrCode.defaultProps = {
  borderVariant: ACTIVE_QR_BORDER_VARIANT,
};

export function profileQrValue(kind: QrKind, channelName: string): string {
  const origin = profileOrigin();
  const channel = encodeURIComponent(channelName);
  if (kind === 'quickcam') return `${origin}/${channel}`;
  return `${origin}/u/${channel}`;
}

export function splitUrlForRow(url: string): { head: string; tail: string } {
  const slash = url.lastIndexOf('/');
  const segment = slash >= 0 ? url.slice(slash) : '';
  if (segment.length > 1 && segment.length <= 16 && segment.length < url.length) {
    return { head: url.slice(0, slash), tail: segment };
  }
  if (url.length <= 12) return { head: url, tail: '' };
  return { head: url.slice(0, -12), tail: url.slice(-12) };
}

export default function ProfileMenu({
  channelName,
  profileImageURL,
  displayName,
  showAuth,
  premium,
}: ProfileMenuProps): JSX.Element {
  const history = useHistory();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [qrKind, setQrKind] = useState<QrKind | null>(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const copyTimer = useRef<number | undefined>(undefined);
  const qrFrame = useRef<HTMLDivElement>(null);
  const picture = profileImageURL.trim();
  const center = qrKind ? qrCenterImage(qrKind, profileImageURL, premium) : '';
  const centerIsPhoto = Boolean(qrKind === 'quicklinks' && premium && picture);
  const url = qrKind ? profileQrValue(qrKind, channelName) : '';
  const urlParts = splitUrlForRow(url);
  let markRadius: string | number = 0;
  if (centerIsPhoto) {
    markRadius = '50%';
  } else if (qrKind === 'quickcam') {
    markRadius = '10%';
  }
  let saveLabel = 'Save QR Code';
  if (saving) saveLabel = 'Saving...';
  else if (saveFailed) saveLabel = "Couldn't save";

  useEffect(() => () => {
    window.clearTimeout(copyTimer.current);
  }, []);

  const closeQr = () => {
    window.clearTimeout(copyTimer.current);
    setCopied(false);
    setSaveFailed(false);
    setSaving(false);
    setQrKind(null);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(false), 2000);
    }).catch(() => setCopied(false));
  };

  const saveCode = () => {
    if (!qrKind || !qrFrame.current) return;
    setSaving(true);
    setSaveFailed(false);
    downloadQrPng(qrFrame.current, qrDownloadName(qrKind, channelName))
      .then(() => setSaving(false))
      .catch(() => {
        setSaving(false);
        setSaveFailed(true);
      });
  };

  return (
    <>
      <IconButton
        aria-label="Open profile menu"
        sx={{ ml: 'auto', color: 'var(--qc-shell-copy, #f8f9ff)' }}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <span className="material-icons-outlined">menu</span>
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        <MenuItem onClick={() => {
          setAnchor(null);
          history.push(`/${encodeURIComponent(channelName)}`);
        }}
        >
          QuickCam
        </MenuItem>
        <MenuItem onClick={() => {
          setAnchor(null);
          setQrKind('quickcam');
        }}
        >
          QuickCam QR Code
        </MenuItem>
        <MenuItem onClick={() => {
          setAnchor(null);
          setQrKind('quicklinks');
        }}
        >
          QuickLinks QR Code
        </MenuItem>
        {showAuth && (
          <MenuItem onClick={() => {
            setAnchor(null);
            localStorage.setItem('referringChannelId', channelName);
            window.location.href = getKeycloakLoginUrl();
          }}
          >
            Login
          </MenuItem>
        )}
        {showAuth && (
          <MenuItem onClick={() => {
            setAnchor(null);
            startProfileSignup(channelName);
          }}
          >
            Sign Up
          </MenuItem>
        )}
      </Menu>
      <Dialog open={qrKind !== null} onClose={() => closeQr()} fullWidth maxWidth="sm">
        {qrKind && (
          <>
            <DialogTitle>
              {qrKind === 'quicklinks' ? 'QuickLinks QR Code' : 'QuickCam QR Code'}
            </DialogTitle>
            <DialogContent>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2,
                px: 1.75,
                py: 1.5,
                border: '1px solid var(--qc-accent-line, rgba(91, 151, 255, 0.28))',
                borderRadius: '14px',
                background: 'var(--qc-shell-surface-strong, #091a31)',
              }}
              >
                <Box
                  title={url}
                  aria-label={url}
                  sx={{
                    display: 'flex',
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    color: 'var(--qc-shell-muted, #f5f7fb)',
                    fontSize: 16,
                    lineHeight: 1.45,
                    userSelect: 'all',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      flex: '0 1 auto',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {urlParts.head}
                  </Box>
                  {urlParts.tail ? (
                    <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {urlParts.tail}
                    </Box>
                  ) : null}
                </Box>
                <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                  <Button
                    size="small"
                    onClick={() => copyLink()}
                    sx={{
                      flexShrink: 0,
                      minWidth: 64,
                      height: 40,
                      px: 2.25,
                      borderRadius: '999px',
                      color: 'var(--qc-accent, #2a9dff)',
                      background: 'var(--qc-accent-soft, rgba(42, 157, 255, 0.12))',
                      fontSize: 17,
                      fontWeight: 700,
                      textTransform: 'none',
                    }}
                  >
                    Copy
                  </Button>
                </Tooltip>
              </Box>
              <Box ref={qrFrame} sx={{ display: 'flex', justifyContent: 'center' }}>
                <ProfileQrCode
                  value={url}
                  size={profileQrSize}
                  center={center}
                  alt={centerIsPhoto ? displayName : 'QuickCam QR'}
                  centerIsPhoto={centerIsPhoto}
                  markRadius={markRadius}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button disabled={saving} onClick={() => saveCode()}>{saveLabel}</Button>
              <Button onClick={() => closeQr()}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
